import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Slide } from '@mui/material';
import { addWsListener, WsMessage } from '../../shared/realtime/ws';
import { NotificationDTO, notificationService } from '../service/notificationService';
import { NotificationToast } from './NotificationToast';

interface Props {
  companyId: string;
  userId?: string;
}

interface ToastNotification extends NotificationDTO {
  timestamp: number; // Para controle de auto-fechar
}

const AUTO_CLOSE_DELAY = 5000; // 5 segundos

export const NotificationToastContainer: React.FC<Props> = ({ companyId, userId }) => {
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const seenNotificationIds = useRef<Set<string>>(new Set());

  // Carregar notificações já vistas para evitar duplicatas
  const loadSeenNotifications = useCallback(async () => {
    if (!companyId) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    try {
      const notifications = await notificationService.getOpen(companyId, userId);
      notifications.forEach(n => seenNotificationIds.current.add(n.id));
    } catch (error) {
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        console.error('Erro ao carregar notificações vistas:', error);
      }
    }
  }, [companyId, userId]);

  useEffect(() => {
    loadSeenNotifications();
  }, [loadSeenNotifications]);

  const handleNewNotification = useCallback(async (msg: WsMessage) => {
    try {
      // Verificar se é uma mensagem do tipo NOTIFICATION com payload
      if (msg.type === 'NOTIFICATION' && 'payload' in msg && msg.payload) {
        // Se o payload contém a notificação completa, usar diretamente
        if (msg.payload && typeof msg.payload === 'object' && 'id' in msg.payload) {
          const notification = msg.payload as NotificationDTO;
          
          // Verificar se já foi exibida
          if (seenNotificationIds.current.has(notification.id)) {
            return;
          }

          // Adicionar aos toasts
          const toastNotification: ToastNotification = {
            ...notification,
            timestamp: Date.now(),
          };

          setToasts(prev => [...prev, toastNotification]);
          seenNotificationIds.current.add(notification.id);

          // Auto-fechar após delay
          setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== notification.id));
          }, AUTO_CLOSE_DELAY);
          return;
        }
      }
      
      // Se não tem payload completo ou não é do tipo NOTIFICATION,
      // buscar notificações abertas e mostrar apenas as novas
      const notifications = await notificationService.getOpen(companyId, userId);
      
      notifications.forEach(notification => {
        if (!seenNotificationIds.current.has(notification.id)) {
          const toastNotification: ToastNotification = {
            ...notification,
            timestamp: Date.now(),
          };

          setToasts(prev => [...prev, toastNotification]);
          seenNotificationIds.current.add(notification.id);

          // Auto-fechar após delay
          setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== notification.id));
          }, AUTO_CLOSE_DELAY);
        }
      });
    } catch (error) {
      console.error('Erro ao processar nova notificação:', error);
    }
  }, [companyId, userId]);

  // Escutar WebSocket para novas notificações
  useEffect(() => {
    if (!companyId) return;

    const unsubscribe = addWsListener((msg: WsMessage) => {
      // Quando chega uma nova notificação via WebSocket
      if (msg.type === 'NOTIFICATION' && msg.company_id === companyId) {
        // O payload pode conter a notificação completa ou apenas o ID
        // Vamos buscar a notificação completa se necessário
        handleNewNotification(msg);
      }
    });

    return unsubscribe;
  }, [companyId, handleNewNotification]);

  const handleClose = useCallback((notificationId: string) => {
    setToasts(prev => prev.filter(t => t.id !== notificationId));
  }, []);

  const handleLike = useCallback(() => {
    // Atualizar badge se necessário
    // O NotificationBell já escuta NOTIFICATION_UPDATED
  }, []);

  const handleDislike = useCallback(() => {
    // Atualizar badge se necessário
    // O NotificationBell já escuta NOTIFICATION_UPDATED
  }, []);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        zIndex: 1400, // Acima de drawers (1300) mas abaixo de modals (1500)
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      {toasts.map((toast, index) => (
        <Slide
          key={toast.id}
          direction="right"
          in={true}
          timeout={300}
          mountOnEnter
          unmountOnExit
        >
          <Box>
            <NotificationToast
              notification={toast}
              companyId={companyId}
              onClose={() => handleClose(toast.id)}
              onLike={handleLike}
              onDislike={handleDislike}
            />
          </Box>
        </Slide>
      ))}
    </Box>
  );
};

